import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { PermissionContext } from 'context/Context';
import { useDispatch, useSelector } from 'react-redux';
import dayjs from 'dayjs';
import { FaBan, FaCar, FaCheck, FaChartBar, FaPlus, FaSearch, FaTimes } from 'react-icons/fa';
import { MdListAlt, MdPending, MdCancel, MdFilterAlt } from 'react-icons/md';
import { Card, Tabs, Button, DateRangePicker, CheckboxMultiSelect, AdvanceFilterDrawer, getEmptyValues } from 'components/common';
import CarBookingGrid from 'components/app/bookings/car/transaction/component/CarBookingGrid';
import { debounce } from 'helpers/debounce';
import { activeEmployeesByNameDebounce } from 'helpers/employeeOptionHelper';
import {
  getAllBookings,
  getWaitingApprovalBookings,
  getApprovedBookings,
  getRejectedBookings,
  getCanceledBookings,
  getAllStatuses,
} from 'redux/booking/car/carBookingSlice';
import { getAllCars } from 'redux/booking/car/carSlice';
import { getAllDrivers } from 'redux/booking/car/driverSlice';
import { getActiveSites } from 'redux/organization/siteSlice';

const SEARCH_KEY = 'id_or_description_or_drop_location_or_pickup_location_cont';
const thisYear = () => ({ startDate: dayjs().startOf('year').format('YYYY-MM-DD'), endDate: dayjs().endOf('year').format('YYYY-MM-DD') });
// status_id → dot color (1 waiting, 2 approved, 3 rejected, 4 canceled, 5 reset)
const STATUS_COLOR = { 1: '#f59e0b', 2: '#22c55e', 3: '#ef4444', 4: '#94a3b8', 5: '#06b6d4' };

// Advanced-filter fields (fields not already covered by the quick filters).
const buildAdvancedFields = (drivers, cars) => [
  { key: 'id', label: 'Booking number', type: 'text', paramKey: 'id_cont', tagName: 'ID#' },
  { key: 'purpose', label: 'Purpose of trip', type: 'text', paramKey: 'description_cont', tagName: 'Purpose' },
  { key: 'driver_ids', label: 'Driver', type: 'multiselect', options: drivers, paramKey: 'driver_transactions_driver_id_in', tagName: 'Driver' },
  { key: 'car_ids', label: 'Car', type: 'multiselect', options: cars, paramKey: 'driver_transactions_car_id_in', tagName: 'Car' },
  { key: 'approved_by', label: 'Approved by', type: 'async_multiselect', loadOptions: activeEmployeesByNameDebounce, paramKey: 'approved_by_in', tagName: 'Approved by' },
  { key: 'approved_date', label: 'Approved date', type: 'date_range', paramKeys: ['approved_date_gteq', 'approved_date_lteq'], tagName: 'Approved date' },
  { key: 'created_date', label: 'Created date', type: 'date_range', paramKeys: ['created_at_gteq', 'created_at_lteq'], tagName: 'Created date' },
  { key: 'created_by', label: 'Created by', type: 'async_multiselect', loadOptions: activeEmployeesByNameDebounce, paramKey: 'user_name_in', tagName: 'Created by' },
  { key: 'travel_booking', label: 'From travel booking', type: 'checkbox', paramKey: 'travel_booking_id_not_null', tagName: 'From travel' },
];

const CarBookingPage = () => {
  const navigate = useNavigate();
  const { permissions = {} } = useContext(PermissionContext);
  const canDashboard = permissions.v514 === true;
  const dispatch = useDispatch();

  useEffect(() => {
    dispatch(getAllStatuses());
    dispatch(getAllCars());
    dispatch(getAllDrivers());
    dispatch(getActiveSites());
  }, [dispatch]);

  const bookingCar = useSelector((s) => s.booking_car);
  const transactionStatuses = bookingCar.transactionStatuses;
  const carData = useSelector((s) => s.booking_setting_car.data);
  const driverData = useSelector((s) => s.booking_setting_driver.data);
  const siteData = useSelector((s) => s.organization_site.activeData);

  const statusOptions = useMemo(() => (transactionStatuses || []).map((i) => ({ value: i.id, label: i.name, color: STATUS_COLOR[i.id] || '#94a3b8' })), [transactionStatuses]);
  const siteOptions = useMemo(() => (siteData || []).map((i) => ({ value: i.id, label: i.name })), [siteData]);
  const driverOptions = useMemo(() => (driverData || []).map((i) => ({ value: i.id, label: i.name })), [driverData]);
  const carOptions = useMemo(() => (carData || []).map((i) => ({ value: i.id, label: i.name })), [carData]);
  const advancedFields = useMemo(() => buildAdvancedFields(driverOptions, carOptions), [driverOptions, carOptions]);

  // Quick-filter state.
  const [searchText, setSearchText] = useState('');
  const [search, setSearch] = useState('');
  const [fromDate, setFromDate] = useState(thisYear());
  const [siteIds, setSiteIds] = useState([]);
  const [statusIds, setStatusIds] = useState([]);

  // Advanced-filter (More Filters) state.
  const [showFilter, setShowFilter] = useState(false);
  const [filterValues, setFilterValues] = useState(() => getEmptyValues(buildAdvancedFields([], [])));
  const [advancedParams, setAdvancedParams] = useState({});

  const applySearch = useRef(debounce((v) => setSearch(v), 500)).current;
  const handleSearch = (e) => { setSearchText(e.target.value); applySearch(e.target.value); };
  const clearSearch = () => { setSearchText(''); setSearch(''); };

  const advancedCount = Object.keys(advancedParams).length;

  const extraParams = useMemo(() => {
    const p = { ...advancedParams };
    if (search) p[SEARCH_KEY] = search;
    if (fromDate.startDate) { p.from_date_gteq = fromDate.startDate; p.from_date_lteq = fromDate.endDate; }
    if (siteIds.length) p.site_id_in = siteIds;
    if (statusIds.length) p.status_id_in = statusIds;
    return p;
  }, [search, fromDate, siteIds, statusIds, advancedParams]);

  const badge = (key) => { const t = bookingCar[key]?.totalRows; return t ? t.toLocaleString() : undefined; };

  const tabs = [
    { label: 'All', icon: <MdListAlt size={14} />, badge: badge('allData'), badgeColor: '#6366f1', content: <CarBookingGrid stateKey="allData" loadThunk={getAllBookings} exportStatus="all" extraParams={extraParams} /> },
    { label: 'Waiting for approval', icon: <MdPending size={14} />, badge: badge('waitingApprovalData'), badgeColor: '#f59e0b', content: <CarBookingGrid stateKey="waitingApprovalData" loadThunk={getWaitingApprovalBookings} exportStatus="waiting_approval" extraParams={extraParams} /> },
    { label: 'Approved', icon: <FaCheck size={13} />, badge: badge('approvedData'), badgeColor: '#22c55e', content: <CarBookingGrid stateKey="approvedData" loadThunk={getApprovedBookings} exportStatus="approved" extraParams={extraParams} /> },
    { label: 'Rejected', icon: <FaBan size={13} />, badge: badge('rejectedData'), badgeColor: '#ef4444', content: <CarBookingGrid stateKey="rejectedData" loadThunk={getRejectedBookings} exportStatus="rejected" extraParams={extraParams} /> },
    { label: 'Canceled', icon: <MdCancel size={14} />, badge: badge('canceledData'), badgeColor: '#94a3b8', content: <CarBookingGrid stateKey="canceledData" loadThunk={getCanceledBookings} exportStatus="canceled" extraParams={extraParams} /> },
  ];

  return (
    <div className="tw-flex tw-flex-col tw-gap-3 tw-p-4">
      <div className="tw-flex tw-items-center tw-justify-between tw-flex-wrap tw-gap-3">
        <div className="tw-flex tw-items-center tw-gap-3">
          <div className="tw-w-1.5 tw-h-10 tw-rounded-sm tw-flex-shrink-0" style={{ background: 'linear-gradient(to bottom, #0ea5e9, #6366f1)' }} />
          <div className="tw-flex tw-items-center tw-gap-2">
            <FaCar className="tw-text-blue-600" size={20} />
            <div>
              <h1 className="tw-mb-0 tw-font-bold tw-text-2xl tw-text-gray-900">Car Bookings</h1>
              <p className="tw-text-gray-500 tw-text-sm tw-mb-0">Manage car booking requests across all statuses.</p>
            </div>
          </div>
        </div>
        <div className="tw-flex tw-items-center tw-gap-2">
          {canDashboard && (
            <Button variant="secondary" size="sm" leftIcon={FaChartBar} onClick={() => navigate('/dashboard/booking/car')}>Dashboard</Button>
          )}
          <Button variant="primary" size="sm" leftIcon={FaPlus} onClick={() => navigate('/booking/car/new-car-booking')}>New Car booking</Button>
        </div>
      </div>

      {/* Shared quick filters — Project Schedule (Order Entry) style */}
      <div className="tw-bg-white tw-rounded-lg tw-shadow-sm tw-border tw-border-slate-200">
        <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-2 tw-px-3 tw-py-2.5">
          <div className="tw-relative tw-flex tw-items-center tw-w-full sm:tw-w-56 sm:tw-flex-shrink-0">
            <FaSearch className="tw-absolute tw-left-3 tw-text-slate-400 tw-pointer-events-none" size={12} />
            <input type="text" value={searchText} onChange={handleSearch} placeholder="ID, location, purpose..."
              className="tw-w-full tw-pl-8 tw-pr-7 tw-py-1.5 tw-text-sm tw-border tw-border-slate-200 tw-rounded focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-primary-400 focus:tw-border-transparent tw-bg-slate-50 hover:tw-bg-white tw-transition-colors tw-text-slate-700 tw-placeholder-slate-400" />
            {searchText && (
              <button type="button" onClick={clearSearch} className="tw-absolute tw-right-2 tw-flex tw-items-center tw-justify-center tw-w-4 tw-h-4 tw-rounded-xl tw-bg-slate-300 hover:tw-bg-slate-400 tw-text-white tw-border-0 tw-cursor-pointer tw-p-0"><FaTimes size={8} /></button>
            )}
          </div>
          <div className="tw-hidden sm:tw-block tw-h-5 tw-w-px tw-bg-slate-200 tw-flex-shrink-0" />
          <DateRangePicker label="From date" startDate={fromDate.startDate} endDate={fromDate.endDate} onChange={setFromDate} />
          <div className="tw-hidden sm:tw-block tw-h-5 tw-w-px tw-bg-slate-200 tw-flex-shrink-0" />
          <CheckboxMultiSelect options={siteOptions} value={siteIds} onChange={setSiteIds} allLabel="All sites" />
          <div className="tw-hidden sm:tw-block tw-h-5 tw-w-px tw-bg-slate-200 tw-flex-shrink-0" />
          <CheckboxMultiSelect options={statusOptions} value={statusIds} onChange={setStatusIds} allLabel="All statuses" />
          <div className="tw-hidden sm:tw-block tw-h-5 tw-w-px tw-bg-slate-200 tw-flex-shrink-0" />
          <button
            type="button"
            onClick={() => setShowFilter(true)}
            className={`tw-inline-flex tw-items-center tw-gap-1.5 tw-px-3 tw-py-1.5 tw-rounded tw-border tw-text-sm tw-font-medium tw-cursor-pointer tw-transition-all tw-flex-shrink-0 ${advancedCount > 0 ? 'tw-bg-primary-600 tw-border-primary-600 tw-text-white hover:tw-bg-primary-700' : 'tw-bg-white tw-border-slate-200 tw-text-slate-600 hover:tw-border-primary-300 hover:tw-text-primary-600 hover:tw-bg-primary-50'}`}
          >
            <MdFilterAlt size={15} /> More Filters
            {advancedCount > 0 && <span className="tw-inline-flex tw-items-center tw-justify-center tw-min-w-[18px] tw-h-[18px] tw-px-1 tw-rounded-xl tw-bg-white/30 tw-text-white tw-text-xs tw-font-bold tw-leading-none">{advancedCount}</span>}
          </button>
        </div>
      </div>

      <Card padding="none">
        <Tabs variant="underline" lazyMount navClassName="tw-px-3 tw-pt-2 tw-bg-white tw-flex-nowrap tw-overflow-x-auto tw-no-scrollbar" contentClassName="tw-bg-slate-50 tw-p-4" tabs={tabs} />
      </Card>

      <AdvanceFilterDrawer
        show={showFilter}
        onClose={() => setShowFilter(false)}
        onApply={(params) => { setAdvancedParams(params); setShowFilter(false); }}
        onReset={() => { setAdvancedParams({}); setFilterValues(getEmptyValues(advancedFields)); }}
        fields={advancedFields}
        values={filterValues}
        onChange={(key, val) => setFilterValues((prev) => ({ ...prev, [key]: val }))}
        title="More filters — car bookings"
      />
    </div>
  );
};

export default CarBookingPage;
